import React from 'react';
import cx from 'clsx';
import { FormattedMessage } from 'react-intl';
import { ContentAttribute, SelectorContent, useSelectorMachineContext } from '@printdeal/product-selector';
import { Service } from '../../../../types/product/productDetailPage';

interface Props {
  product: SelectorContent['product'];
  services?: Service[];
  className?: string;
  titleClass?: string;
  valueClass?: string;
}

interface AttributesProps extends Omit<Props, 'product'> {
  name: string;
  selectedAttribute?: ContentAttribute;
  value: string;
}

interface GroupAttributesProps extends Omit<Props, 'product'> {
  name: string;
  groupAttributeValues: {
    label: string,
    quantity: number
  }[];
  rowProductAttribute?: ContentAttribute;
  columnProductAttribute?: ContentAttribute;
}

const getProductAttribute = (
  productAttributes: ContentAttribute[],
  attributeName: string,
): ContentAttribute | undefined =>
  productAttributes.find(({ pimAttributeName }) => pimAttributeName === attributeName);

const Attributes = ({
  name,
  selectedAttribute,
  value,
  className,
  titleClass,
  valueClass,
}: AttributesProps) => {
  const selectedValue = selectedAttribute?.values?.find((attributeValue) => attributeValue.pimAttributeValue === value);

  return (
    <div
      key={selectedAttribute?.pimAttributeName}
      data-cy-attribute-name={selectedAttribute?.pimAttributeName || name}
      className={`pd-grid pd-grid-cols-2 sm:pd-grid-cols-3 ${className}`}
    >
      <div className={`pd-col-span-2 sm:pd-col-span-1 ${titleClass}`}>
        {selectedAttribute?.attributeAlias || selectedAttribute?.pimAttributeName || name}
      </div>
      <div
        className={`pd-col-span-2 ${valueClass}`}
        data-cy-attribute-value={selectedValue?.pimAttributeValue || value}
      >
        {selectedValue?.attributeValueAlias || selectedValue?.pimAttributeValue || value}
      </div>
    </div>
  );
};

const GroupAttributes = ({
  name,
  groupAttributeValues,
  rowProductAttribute,
  columnProductAttribute,
  className,
  titleClass,
  valueClass,
}: GroupAttributesProps) => {
  const getTranslatedLabel = (label: string) => rowProductAttribute?.values?.find(
    (attributeValue) => attributeValue.pimAttributeValue === label,
  )?.attributeValueAlias || label;

  const getTranslatedColumn = (columnName: string) => columnProductAttribute?.values?.find(
    (attributeValue) => attributeValue.pimAttributeValue === columnName,
  )?.attributeValueAlias || columnName;

  const translatedColumn = getTranslatedColumn(name);

  return (
    <div key={name} data-cy-pivot-x={name} className={`pd-grid pd-grid-cols-2 sm:pd-grid-cols-3 ${className}`}>
      <div className={`pd-col-span-2 sm:pd-col-span-1 ${titleClass}`}>
        {rowProductAttribute?.attributeAlias || rowProductAttribute?.pimAttributeName} {'\n'}
        {translatedColumn && `${translatedColumn.toLowerCase()}`}
      </div>
      <div className={valueClass}>
        {groupAttributeValues.map(({ label, quantity }, index) => {
          const translatedLabel = getTranslatedLabel(label);
          return (
            <span key={translatedLabel} data-cy-pivot-y={translatedLabel}>
              {index > 0 && ', '}
              {translatedLabel}: <span data-cy-quantity={quantity}>{quantity}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
};

const ATTRIBUTES_TO_IGNORE = [
  'production time',
  'allowquantityrangesoverwrite',
];

const Selection = ({
  product, services = [], className = '', titleClass = 'pd-text-dim-500', valueClass = '',
}: Props) => {
  const selection = useSelectorMachineContext((state) => state.context.selection);
  const group = useSelectorMachineContext((state) => state.context.group);
  const productAttributes = useSelectorMachineContext((state) => state.context.attributes);
  const selectorAttributes = useSelectorMachineContext((state) => state.context.productAttributes);
  const artworkRequired = useSelectorMachineContext((state) => state.context.artworkRequired);
  const totalQuantity = useSelectorMachineContext((state) => state.context.totalQuantity);
  const groupAttributes = group?.filter(({ quantity }) => quantity);
  const gridAttribute = productAttributes.find((attribute) => attribute.grid);

  const serviceKey = useSelectorMachineContext((state) => state.context.selectedService);
  const selectedService = services?.find((service) => service.key === serviceKey);

  const combinedGroup = groupAttributes?.reduce((combinedAttributes, { attributes, quantity }) => {
    const pivot = gridAttribute?.grid?.column.name;
    const label = gridAttribute?.grid?.row.name;

    if (!pivot || !label) return {};
    if (!combinedAttributes[attributes[pivot]]) {
      return { ...combinedAttributes, [attributes[pivot]]: [{ label: attributes[label], quantity }] };
    }
    return {
      ...combinedAttributes,
      [attributes[pivot]]: [...combinedAttributes[attributes[pivot]], { label: attributes[label], quantity }],
    };
  }, {} as { [key: string]: { label: string, quantity: number }[] });

  const rowProductAttribute = gridAttribute?.grid?.row?.name
    ? getProductAttribute(product.attributes, gridAttribute.grid.row.name)
    : undefined;

  const columnProductAttribute = gridAttribute?.grid?.column?.name
    ? getProductAttribute(product.attributes, gridAttribute?.grid?.column?.name)
    : undefined;

  const sortedSelection = productAttributes
    .filter((attribute) => !ATTRIBUTES_TO_IGNORE.includes(attribute.name.toLowerCase()))
    .reduce((acc, productAttribute) => {
      const { name, type } = productAttribute;
      if (type === 'group') {
        const { attributes } = productAttribute;
        attributes.forEach((attribute) => {
          const resolvedValue = selectorAttributes?.find(({ key }) => key === attribute.name)?.resolvedValue;
          acc[attribute.name] = selection[attribute.name] || resolvedValue || '';
        });
      } else {
        acc[name] = selection[name];
      }
      return acc;
    }, {} as Record<string, string>);

  return (
    <>
      {Object.entries(sortedSelection).map(([key, value]) => {
        const selectedAttribute = product.attributes.find((attribute) => attribute.pimAttributeName === key);

        return (
          <Attributes
            key={key}
            name={key}
            selectedAttribute={selectedAttribute}
            value={value}
            className={className}
            titleClass={titleClass}
            valueClass={valueClass}
          />
        );
      })}

      <div className={cx('pd-grid pd-grid-cols-2 sm:pd-grid-cols-3', className)}>
        <div className={`pd-col-span-2 sm:pd-col-span-1 ${titleClass}`}>
          <FormattedMessage id="selector.quantity.title" defaultMessage="Quantity" />
        </div>
        <div className={`pd-col-span-2 ${valueClass}`}>{selection.Quantity || totalQuantity}</div>
      </div>

      {Object.entries(combinedGroup || {}).map(([key, groupAttributeValues]) => (
        <GroupAttributes
          key={key}
          name={key}
          groupAttributeValues={groupAttributeValues}
          rowProductAttribute={rowProductAttribute}
          columnProductAttribute={columnProductAttribute}
          className={className}
          titleClass={titleClass}
          valueClass={valueClass}
        />
      ))}
      {artworkRequired && (
        <div className={cx('pd-grid pd-grid-cols-2 sm:pd-grid-cols-3', className)}>
          <div className={`pd-col-span-2 sm:pd-col-span-1 ${titleClass}`}>
            <FormattedMessage id="product.configurator.summary.numberOfDesigns" defaultMessage="Number of designs" />
          </div>
          <div className={`pd-col-span-2 ${valueClass}`}>1</div>
        </div>
      )}
      {selectedService && (
        <div className={cx('pd-grid pd-grid-cols-2 sm:pd-grid-cols-3', className)}>
          <div className={`pd-col-span-2 sm:pd-col-span-1 ${titleClass}`}>
            <FormattedMessage id="selector.service.title" defaultMessage="Service" />
          </div>
          <div className={`pd-col-span-2 ${valueClass}`}>
            {selectedService.displayName}
          </div>
        </div>
      )}
    </>
  );
};

export default Selection;
